/**
 * Builds a GTM item object from cart item data
 * @param {number} variantId - Variant ID
 * @param {object} itemData - Cart item data
 * @param {number} quantity - Quantity
 * @param {object} gtmItemConfig - GTM config for this item
 * @returns {object} GTM item object
 */
const buildGtmItem = (itemData, quantity, gtmItemConfig = {}) => {
    const itemPrice = (itemData.final_price || 0) / 100;
    
    return {
      item_id: gtmItemConfig.item_id || itemData.product_id || '',
      item_name: gtmItemConfig.item_name || itemData.product_title || '',
      item_brand: gtmItemConfig.item_brand || itemData.vendor || '',
      item_category: gtmItemConfig.item_category || '',
      item_category2: gtmItemConfig.item_category2 || '',
      item_category3: gtmItemConfig.item_category3 || '',
      item_category4: gtmItemConfig.item_category4 || '',
      item_category5: gtmItemConfig.item_category5 || '',
      item_variant: gtmItemConfig.item_variant || '',
      price: gtmItemConfig.price || itemPrice,
      quantity
    };
}
  
/**
 * Tracks an ecommerce event in GTM dataLayer
 * @param {string} eventName - GTM event name (e.g. 'add_to_cart', 'remove_from_cart', 'view_cart')
 * @param {object} cartData - Cart data containing items
 * @param {number} [variantId] - Optional: Variant ID for single-item events
 * @param {number} quantity - Optional: Quantity for single-item events
 */
const trackCartAction = (eventName, cartData, variantId, quantity) => {
    if (typeof window.dataLayer === 'undefined') {
        console.error(`${eventName} event - dataLayer not available.`);
        return;
    }

    if (!cartData || !cartData.items) {
        console.error(`${eventName} event - invalid cart data.`);
        return;
    }

    try {
        const currency = window.gtmConfig?.currency || 'CZK';
        const items = [];
        let totalValue = 0;

        const isSingleItemEvent = variantId !== undefined && quantity !== undefined;
        if (isSingleItemEvent) {
            let itemData = cartData.items.find(item => item.variant_id === variantId);
            const gtmItemConfig = window.gtmConfig?.items?.[variantId] || {};

            if (!itemData) {
              if (Object.keys(gtmItemConfig).length === 0) {
                console.error(`${eventName} event - itemData not found for variant ${variantId}`);
                return;
              }
              // Item was fully removed from cart; gtmItemConfig has all fields needed by buildGtmItem
              itemData = {};
            }

            const item = buildGtmItem(itemData, quantity, gtmItemConfig);
            items.push(item);
            totalValue = item.price * quantity;
        } else {
            if (cartData.items.length === 0) {
              console.error(`${eventName} event - cart is empty, skipping tracking.`);
              return;
            }

            cartData.items.forEach(itemData => {
              const gtmItemConfig = window.gtmConfig?.items?.[itemData.variant_id] || {};
              const item = buildGtmItem(itemData, itemData.quantity, gtmItemConfig);
              items.push(item);
              totalValue += item.price * item.quantity;
            });
        }


        const ecommerceData = { currency, items, value: totalValue };

        window.dataLayer.push({
            event: eventName,
            ecommerce: ecommerceData,
            _clear: true
        });

        console.info(`✓ GTM event ${eventName} pushed to dataLayer.`);
    } catch (error) {
        console.error(`Error tracking ${eventName}:`, error);
    }
}

/**
 * Wait for mx-boost enrichment to populate data-gtm-item on a card.
 * Resolves immediately if the data is already present.
 * @param {HTMLElement} card - Boost product card element
 * @param {number} [maxWait=5000] - Maximum wait time in ms
 * @returns {Promise<void>}
 */
const waitForCardGtmItem = (card, maxWait = 5000) => {
  return new Promise(resolve => {
    if (card.dataset.gtmItem) {
      resolve();
      return;
    }
    let timeoutId;
    const handler = (e) => {
      if (e.detail && e.detail.productId === card.dataset.productId) {
        document.removeEventListener('on:boost:card:update', handler);
        clearTimeout(timeoutId);
        resolve();
      }
    };
    document.addEventListener('on:boost:card:update', handler);
    timeoutId = setTimeout(() => {
      document.removeEventListener('on:boost:card:update', handler);
      resolve();
    }, maxWait);
  });
};

/**
 * Extract GTM data from product card DOM element - Boost
 * @param {HTMLElement} card - Product card element
 * @returns {Promise<object|null>} GTM item data
 */
const extractGtmDataFromCard = async (card) => {
  try {
    if (!card.dataset.product) {
      console.warn('No data-product attribute found on card');
      return null;
    }

    let parsedProduct;
    try {
      parsedProduct = JSON.parse(card.dataset.product);
    } catch (jsonError) {
      console.warn('Failed to parse product JSON, skipping card:', card.dataset.productId);
      return null;
    }

    const productName = parsedProduct.title || '';
    const vendor = parsedProduct.vendor || '';

    let stockStatus = 'Vyprodáno';
    if (parsedProduct.available) {
      stockStatus = 'Skladem';
    }

    // Wait for mx-boost enrichment to provide per-product GTM data
    await waitForCardGtmItem(card);

    let categories;
    let price;
    let tax;
    if (card.dataset.gtmItem) {
      try {
        const gtmItem = JSON.parse(card.dataset.gtmItem);
        categories = {
          item_category: gtmItem.item_category || '',
          item_category2: gtmItem.item_category2 || '',
          item_category3: gtmItem.item_category3 || '',
          item_category4: gtmItem.item_category4 || '',
          item_category5: gtmItem.item_category5 || ''
        };
        stockStatus = gtmItem.item_variant || stockStatus;
        price = gtmItem.price;
        tax = gtmItem.tax;
      } catch (e) {
        categories = extractCategoriesFromBreadcrumbsListPage();
        price = Math.round((Number(parsedProduct.priceMin) / 1.21) * 100) / 100 || 0;
        tax = Number(parsedProduct.tax) || 0;
      }
    } else {
      categories = extractCategoriesFromBreadcrumbsListPage();
      price = Math.round((Number(parsedProduct.priceMin) / 1.21) * 100) / 100 || 0;
      tax = Number(parsedProduct.tax) || 0;
    }

    return {
      item_id: (card.dataset.productId || parsedProduct.id || '').toString(),
      item_name: productName,
      item_brand: vendor,
      item_category: categories.item_category,
      item_category2: categories.item_category2,
      item_category3: categories.item_category3,
      item_category4: categories.item_category4,
      item_category5: categories.item_category5,
      item_variant: stockStatus,
      price: price,
      tax: tax
    };
  } catch (error) {
    console.error('Error extracting GTM data from card:', error);
    return null;
  }
}

const extractCategoriesFromBreadcrumbsListPage = () => {
  const categories = {
    item_category: '',
    item_category2: '',
    item_category3: '',
    item_category4: '',
    item_category5: ''
  };
  
  try {
    const breadcrumbsList = document.querySelector('.breadcrumbs-list');
    if (breadcrumbsList) {
      const links = breadcrumbsList.querySelectorAll('.breadcrumbs-list__link');
      const categoryNames = [];

      for (let i = 1; i < links.length; i++) {
        const text = links[i].textContent.trim();
        if (text) {
          categoryNames.push(text);
        }
      }
      
      categoryNames.forEach((name, index) => {
        if (index === 0) categories.item_category = name;
        else if (index === 1) categories.item_category2 = name;
        else if (index === 2) categories.item_category3 = name;
        else if (index === 3) categories.item_category4 = name;
        else if (index === 4) categories.item_category5 = name;
      });
      
      return categories;
    }
  } catch (error) {
    console.error('Error extracting categories from breadcrumbs:', error);
  }

  return categories;
}

/**
 * Extract GTM data from native product card (non-Boost)
 * @param {HTMLElement} card - Product card element
 * @returns {object|null} GTM item data
 */
const extractGtmDataFromNativeCard = (card) => {
  try {
    const productId = card.dataset.gtmProductId;
    if (!productId) {
      return null;
    }

    const productTitle = card.dataset.gtmProductTitle;
    const productVendor = card.dataset.gtmProductVendor;
    const productPrice = Math.round((parseFloat(card.dataset.gtmProductPrice) / 1.21) * 100) / 100 || 0;
    const productAvailable = card.dataset.gtmProductAvailable === 'true';
    const stockStatus = productAvailable ? 'Skladem' : 'Vyprodáno';

    return {
      item_id: productId,
      item_name: productTitle || '',
      item_brand: productVendor || '',
      item_category: '',
      item_category2: '',
      item_category3: '',
      item_category4: '',
      item_category5: '',
      item_variant: stockStatus,
      price: productPrice
    };
  } catch (error) {
    console.warn('Error extracting GTM data from native card:', error);
    return null;
  }
};

/**
 * Push view_item_list event to dataLayer
 * @param {Array} items - Array of GTM item objects
 */
const trackViewItemList = (items) => {
  const EVENT_NAME = 'view_item_list';

  try { 
    if (typeof window.dataLayer === 'undefined') {
      console.error(`${EVENT_NAME} - dataLayer not available.`);
      return;
    }

    if (!items || items.length === 0) {
      return;
    }

    const listName = window.gtmConfig?.listName || '';
    const listId = window.gtmConfig?.listId || '';
    const currency = window.gtmConfig?.currency;

    window.dataLayer.push({
      event: EVENT_NAME,
      ecommerce: {
        currency: currency,
        item_list_name: listName,
        item_list_id: listId,
        items
      },
      _clear: true
    });

    console.info(`✓ GTM event ${EVENT_NAME} pushed to dataLayer.`);
  } catch (error) {
    console.error(`Error tracking ${EVENT_NAME}:`, error);
  }
};

/**
 * Initialize product impressions tracking
 * @param {object} config - Configuration object
 * @param {string} config.selector - CSS selector for product cards
 * @param {Function} config.extractData - Function to extract GTM data from card
 * @param {Function} [config.waitCondition] - Optional function that returns true when ready to start
 * @param {number} [config.batchDelay] - Delay before flushing batch (default: 2000ms)
 */
const initProductImpressions = (config) => {
  const { selector, extractData, waitCondition, batchDelay = 2000 } = config;
  
  const impressionBatches = {};
  const batchTimeouts = {};
  const rafScheduled = {};

  const flushImpressions = (listName) => {
    if (!impressionBatches[listName] || impressionBatches[listName].length === 0) {
      return;
    }

    trackViewItemList(impressionBatches[listName]);
    impressionBatches[listName] = [];
    rafScheduled[listName] = false;
  };

  const scheduleBatchFlush = (listName) => {
    if (!rafScheduled[listName]) {
      rafScheduled[listName] = true;
      requestAnimationFrame(() => {
        if (batchTimeouts[listName]) {
          clearTimeout(batchTimeouts[listName]);
        }
        
        batchTimeouts[listName] = setTimeout(() => {
          flushImpressions(listName);
        }, batchDelay);
      });
    }
  };

  const addToImpressionBatch = async (card) => {
    const listName = window.gtmConfig?.listName || '';
    
    const gtmData = await extractData(card);
    if (!gtmData) {
      return;
    }

    const allCards = document.querySelectorAll(selector);
    const index = Array.from(allCards).indexOf(card) + 1;
    gtmData.index = index;

    if (!impressionBatches[listName]) {
      impressionBatches[listName] = [];
    }

    impressionBatches[listName].push(gtmData);
    card.dataset.impressionTracked = 'true';
    scheduleBatchFlush(listName);
  };

  const handleIntersection = (entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.target.dataset.impressionTracked !== 'true') {
        addToImpressionBatch(entry.target);
      }
    });
  };

  const observer = new IntersectionObserver(handleIntersection, {
    threshold: 0.01,
    rootMargin: '0px'
  });

  const observeProducts = () => {
    const productCards = document.querySelectorAll(`${selector}:not([data-impression-tracked="true"])`);
    productCards.forEach((card) => {
      observer.observe(card);
    });
  };

  const startObserving = () => {
    observeProducts();
    
    const mutationObserver = new MutationObserver(() => {
      observeProducts();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const init = () => {
    if (waitCondition) {
      const checkCondition = (attempts = 0, maxAttempts = 20) => {
        if (waitCondition() || attempts >= maxAttempts) {
          startObserving();
        } else {
          setTimeout(() => checkCondition(attempts + 1, maxAttempts), 200);
        }
      };
      checkCondition();
    } else {
      startObserving();
    }
  };

  init();

  // 'pagehide' instead of 'beforeunload' so the page stays eligible for the bfcache.
  window.addEventListener('pagehide', () => {
    Object.keys(impressionBatches).forEach((listName) => {
      flushImpressions(listName);
    });
  });
}
